import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { FORTA_BLACK, FORTA_BLUE, FORTA_WHITE } from '../../../../../../utils/colors';
import FHDurationInput from '../../../../../../components/base/FHDurationInput';
import { TargetType } from '../../../../../../constants';
import { FHTooltip } from '../../../../../../components/base';
import { capitalizeFirstLetter } from '../../../../../../helpers';

import type {
  ISessionPracticedTargetTrial,
  ITreatmentPlanGoalTarget,
} from '../../../../../../utils/types';

interface TargetPracticeInputProps {
  variant: TargetType;
  isEditable: boolean;
  savingFailed: boolean;
  savingInProgress: boolean;
  trial: ISessionPracticedTargetTrial;
  target?: ITreatmentPlanGoalTarget;
  onTrialDataChange: (trialDataDiff: any) => void;
}

const TargetPracticeInputReadOnly: React.FC<TargetPracticeInputProps> = (props) => {
  const { t } = useTranslation('sessionNotes');
  const {
    variant,
    trial,
    target,
    isEditable = true,
    savingFailed = false,
    savingInProgress = false,
    onTrialDataChange,
  } = props;
  const [numSuccesses, setNumSuccesses] = useState<number>(0);
  const [numFailures, setNumFailures] = useState<number>(0);
  const [numInstances, setNumInstances] = useState<number>(0);

  useEffect(() => {
    if (!trial) return;
    if (variant === TargetType.SuccessFailure) {
      if (Number.isInteger(trial?.attempts)) {
        setNumFailures(trial.attempts - (trial?.expected_response ?? 0));
      }
      if (Number.isInteger(trial?.expected_response)) {
        setNumSuccesses(trial.expected_response);
      }
    }
    if (variant === TargetType.Instances) {
      if (Number.isInteger(trial?.occurrence)) {
        setNumInstances(trial.occurrence);
      }
    }
    if (variant === TargetType.InstancesWithDuration) {
      //TBD update this after api endpoint is ready
    }
  }, [trial, variant]);

  useEffect(() => {
    if (savingFailed) {
      if (variant === TargetType.SuccessFailure) {
        if (Number.isInteger(trial?.attempts)) {
          setNumFailures(trial.attempts - (trial?.expected_response ?? 0));
        }
        if (Number.isInteger(trial?.expected_response)) {
          setNumSuccesses(trial.expected_response);
        }
      }
      if (variant === TargetType.Instances) {
        if (Number.isInteger(trial?.occurrence)) {
          setNumInstances(trial.occurrence);
        }
      }
      if (variant === TargetType.InstancesWithDuration) {
        //TBD update this after api endpoint is ready
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingFailed]);

  const renderInstanceWithDuration = () => {
    return (
      isEditable && (
        <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
            <Typography
              sx={{
                fontSize: '16px',
                textAlign: 'left',
                color: FORTA_BLACK,
                lineHeight: '24px',
              }}
            >
              <strong>Duration</strong> (of instance)
            </Typography>
            <FHTooltip
              title="Your BCBA is requiring you to record the instance’s duration. Each recorded duration will count as an instance."
              arrow
              placement="top"
              maxwidth={310}
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ ml: 1, mb: '2px', color: FORTA_BLUE.primary }}
              />
            </FHTooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
            }}
          >
            <FHDurationInput
              dataTestId={`InputDurationTarget${capitalizeFirstLetter(
                trial?.target?.label?.split(' ')?.join(''),
              )}`}
              isDisabled={!isEditable || savingInProgress}
              reset={savingInProgress}
              durationType={
                target?.event_duration_measurement_unit
                  ? `${target.event_duration_measurement_unit.toLowerCase()}s`
                  : ''
              }
              onAdd={(durationToAdd) => {
                onTrialDataChange({
                  instances: [...(trial?.instances ?? []), { duration: durationToAdd }],
                  occurrence: (trial?.instances?.length ?? 0) + 1,
                });
              }}
            />
          </Box>
        </Box>
      )
    );
  };

  const renderRate = () => {
    return (
      isEditable && (
        <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
            <Typography
              sx={{
                fontSize: '16px',
                textAlign: 'left',
                color: FORTA_BLACK,
                lineHeight: '24px',
              }}
            >
              <strong>Duration</strong> (of instance)
            </Typography>
            <FHTooltip
              title="Your BCBA is requiring you to record the instance’s duration. Each recorded duration will count as an instance."
              arrow
              placement="top"
              maxwidth={310}
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ ml: 1, mb: '2px', color: FORTA_BLUE.primary }}
              />
            </FHTooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
            }}
          >
            <FHDurationInput
              dataTestId={`InputDurationTarget${capitalizeFirstLetter(
                trial?.target?.label?.split(' ')?.join(''),
              )}`}
              isDisabled={!isEditable || savingInProgress}
              reset={savingInProgress}
              durationType={
                target?.event_duration_measurement_unit
                  ? `${target.event_duration_measurement_unit.toLowerCase()}s`
                  : ''
              }
              onAdd={(durationToAdd) => {
                onTrialDataChange({
                  instances: [...(trial?.instances ?? []), { duration: durationToAdd }],
                  occurrence: (trial?.instances?.length ?? 0) + 1,
                });
              }}
            />
          </Box>
        </Box>
      )
    );
  };

  const renderSuccessFailure = () => {
    return (
      <>
        <Box
          sx={{
            mt: 1,
            ml: { sm: 2, xs: 0.2 },
            p: 2,
            borderRadius: '8px',
            border: `1px solid ${FORTA_BLUE.light2}`,
            backgroundColor: FORTA_WHITE,
            width: '100%',
          }}
        >
          <Typography fontSize={16} fontWeight={700} display="inline">
            {`${t('goalsPracticed.unsuccessful')}: `}
          </Typography>
          <Typography fontSize={16} display="inline">
            {`${numFailures}`}
          </Typography>
          <Typography fontSize={16} fontWeight={700} ml={2} display="inline">
            {`${t('goalsPracticed.successful')}: `}
          </Typography>
          <Typography fontSize={16} display="inline">
            {`${numSuccesses}`}
          </Typography>
          <Typography fontSize={16} fontWeight={700} ml={2} display="inline">
            {`${t('goalsPracticed.totalAttempts')}: `}
          </Typography>
          <Typography fontSize={16} display="inline">
            {numSuccesses + numFailures}
          </Typography>
        </Box>
      </>
    );
  };

  const renderInstances = () => {
    return (
      <Box
        sx={{
          mt: 1,
          ml: { sm: 2, xs: 0.2 },
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${FORTA_BLUE.light2}`,
          backgroundColor: FORTA_WHITE,
          width: '100%',
        }}
      >
        <Typography fontWeight={700} display="inline">{`${t(
          'goalsPracticed.instanceTracker',
        )}: `}</Typography>
        <Typography display="inline">{`${numInstances} ${t(
          'goalsPracticed.instances',
        )}`}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ pr: 3 }}>
      {variant === TargetType.Instances
        ? renderInstances()
        : variant === TargetType.InstancesWithDuration
          ? renderInstanceWithDuration()
          : variant === TargetType.SuccessFailure
            ? renderSuccessFailure()
            : variant === TargetType.Rate && renderRate()}
    </Box>
  );
};

export default TargetPracticeInputReadOnly;
